import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useSetupStatus } from '@/hooks/useSetup'
import { USER_ROLE } from '@/utils/constants'
import Layout from '@/components/Layout'
import Landing from '@/pages/Landing'
import PasswordReset from '@/pages/PasswordReset'
import PasswordResetConfirm from '@/pages/PasswordResetConfirm'
import OAuth2Callback from '@/pages/OAuth2Callback'
import Setup from '@/pages/Setup'
import Dashboard from '@/pages/Dashboard'
import ApiKeyPage from '@/pages/ApiKey'
import AvailableModels from '@/pages/AvailableModels'
import Log from '@/pages/Log'
import Channel from '@/pages/Channel'
import User from '@/pages/User'
import ModelPage from '@/pages/Model'
import ModelDeployment from '@/pages/ModelDeployment'
import Redemption from '@/pages/Redemption'
import Subscription from '@/pages/Subscription'
import TopUp from '@/pages/TopUp'
import TopUpOrders from '@/pages/TopUpOrders'
import Personal from '@/pages/Personal'
import Pricing from '@/pages/Pricing'
import Setting from '@/pages/Setting'
import Playground from '@/pages/Playground'
import Chat from '@/pages/Chat'
import Midjourney from '@/pages/Midjourney'
import Task from '@/pages/Task'
import About from '@/pages/About'
import Docs from '@/pages/Docs'
import PrivacyPolicy from '@/pages/PrivacyPolicy'
import UserAgreement from '@/pages/UserAgreement'
import NotFound from '@/pages/NotFound'
import Forbidden from '@/pages/Forbidden'
function SetupCheck({ children }: { children: React.ReactNode }) {
  const { data: setupStatus, isLoading } = useSetupStatus()
  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">加载中...</div>
  }
  if (setupStatus && !setupStatus.initialized) {
    return <Navigate to="/setup" replace />
  }
  return <>{children}</>
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/" replace />
  if ((user?.role ?? 0) < USER_ROLE.ADMIN) return <Navigate to="/forbidden" replace />
  return <>{children}</>
}

function RootRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/" replace />
  if ((user?.role ?? 0) < USER_ROLE.ROOT) return <Navigate to="/forbidden" replace />
  return <>{children}</>
}

function LegacyAuthRedirect({ mode }: { mode: 'login' | 'register' }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const location = useLocation()

  if (isAuthenticated) {
    return <Navigate to="/console" replace />
  }

  const searchParams = new URLSearchParams(location.search)
  searchParams.set('auth', mode)

  return <Navigate to={{ pathname: '/', search: `?${searchParams.toString()}` }} replace />
}

function SetupGuard({ children }: { children: React.ReactNode }) {
  const { data: setupStatus, isLoading } = useSetupStatus()
  if (isLoading) {
    return <div className="flex items-center justify-center h-screen">加载中...</div>
  }
  if (setupStatus?.initialized) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

function LandingRoute({ children }: { children: React.ReactNode }) {
  // 所有用户（包括已登录）都可以访问门户首页
  return <>{children}</>
}

export const router = createBrowserRouter([
  // 公开页
  {
    path: '/',
    element: (
      <SetupCheck>
        <LandingRoute>
          <Landing />
        </LandingRoute>
      </SetupCheck>
    ),
  },
  {
    path: '/setup',
    element: (
      <SetupGuard>
        <Setup />
      </SetupGuard>
    ),
  },
  {
    path: '/login',
    element: (
      <SetupCheck>
        <LegacyAuthRedirect mode="login" />
      </SetupCheck>
    ),
  },
  {
    path: '/Login',
    element: (
      <SetupCheck>
        <LegacyAuthRedirect mode="login" />
      </SetupCheck>
    ),
  },
  {
    path: '/register',
    element: (
      <SetupCheck>
        <LegacyAuthRedirect mode="register" />
      </SetupCheck>
    ),
  },
  {
    path: '/Register',
    element: (
      <SetupCheck>
        <LegacyAuthRedirect mode="register" />
      </SetupCheck>
    ),
  },
  {
    path: '/password-reset',
    element: (
      <SetupCheck>
        <PasswordReset />
      </SetupCheck>
    ),
  },
  {
    path: '/password-reset/confirm',
    element: (
      <SetupCheck>
        <PasswordResetConfirm />
      </SetupCheck>
    ),
  },
  {
    path: '/oauth2/callback/:provider',
    element: <OAuth2Callback />,
  },
  {
    path: '/oauth/callback/:provider',
    element: <OAuth2Callback />,
  },
  { path: '/oauth/github', element: <OAuth2Callback /> },
  { path: '/oauth/discord', element: <OAuth2Callback /> },
  { path: '/oauth/oidc', element: <OAuth2Callback /> },
  { path: '/oauth/linuxdo', element: <OAuth2Callback /> },
  { path: '/oauth/:provider', element: <OAuth2Callback /> },
  {
    path: '/pricing',
    element: (
      <SetupCheck>
        <Pricing />
      </SetupCheck>
    ),
  },
  { path: '/about', element: <About /> },
  { path: '/docs', element: <Docs /> },
  { path: '/policy', element: <PrivacyPolicy /> },
  { path: '/agreement', element: <UserAgreement /> },
  { path: '/forbidden', element: <Forbidden /> },

  // 控制台
  {
    path: '/console',
    element: (
      <SetupCheck>
        <PrivateRoute>
          <Layout />
        </PrivateRoute>
      </SetupCheck>
    ),
    children: [
      { index: true, element: <Dashboard /> },
      { path: 'api-keys', element: <ApiKeyPage /> },
      { path: 'token', element: <Navigate to="/console/api-keys" replace /> },
      { path: 'available-models', element: <AvailableModels /> },
      { path: 'log', element: <Log /> },
      { path: 'usage', element: <Navigate to="/console/log" replace /> },
      { path: 'personal', element: <Personal /> },
      { path: 'settings', element: <Navigate to="/console/personal" replace /> },
      { path: 'topup', element: <TopUp /> },
      { path: 'midjourney', element: <Midjourney /> },
      { path: 'task', element: <Task /> },
      { path: 'playground', element: <Playground /> },
      { path: 'chat', element: <Chat /> },
      { path: 'chat/:id', element: <Chat /> },

      // Admin
      { path: 'channel', element: <AdminRoute><Channel /></AdminRoute> },
      { path: 'channels', element: <Navigate to="/console/channel" replace /> },
      { path: 'user', element: <AdminRoute><User /></AdminRoute> },
      { path: 'users', element: <Navigate to="/console/user" replace /> },
      { path: 'models', element: <AdminRoute><ModelPage /></AdminRoute> },
      { path: 'deployment', element: <AdminRoute><ModelDeployment /></AdminRoute> },
      { path: 'redemption', element: <AdminRoute><Redemption /></AdminRoute> },
      { path: 'topup-orders', element: <AdminRoute><TopUpOrders /></AdminRoute> },
      { path: 'subscription', element: <AdminRoute><Subscription /></AdminRoute> },

      // Root
      { path: 'setting', element: <RootRoute><Setting /></RootRoute> },
    ],
  },

  { path: '*', element: <NotFound /> },
])
